import { MDText } from 'i18n-react';
import Link from 'next/link';

import { Button } from '@motorway/mw-highway-code';

import cypressIds from 'Components/cypress_ids.json';
import { useBreakpoint } from 'Context/BreakpointsContext/BreakpointsContext';
import { useVehicleData } from 'Context/VehicleContext/VehicleDataContext';
import { triggerPostSaleActionClickEvent } from 'Services/analytics/events';
import { applyCypressData } from 'Utilities/helpers';
import ROUTES from 'Utilities/routes';

import soldTexts from './Sold.texts';
import { DELIVERY_ACTIONS, useDeliveryAction } from './useDeliveryAction';

import styles from './Sold.module.scss';

const LocalT = new MDText(soldTexts);

const SellerDetails = ({ isDesktop }: { isDesktop: boolean }) => {
	const { id } = useVehicleData();
	const label = `${LocalT.translate(`postSaleCTA.sellerDetails.${isDesktop ? 'desktop' : 'mobile'}`)}`;

	const handleSellerDetailsClick = () => {
		triggerPostSaleActionClickEvent({ enquiryId: id, eventType: 'seller_details_button' });
	};

	return (
		<Link legacyBehavior passHref as={ROUTES.SELLER_DETAILS.as(id)} href={ROUTES.SELLER_DETAILS.href}>
			<Button
				data-thc-button
				fullWidth
				as="a"
				label={label}
				onClick={handleSellerDetailsClick}
				variant="secondary"
				{...applyCypressData(cypressIds.buttons.postSaleActions.viewSellerDetails)}
			/>
		</Link>
	);
};

interface DeliveryCTAProps {
	deliveryAction: (typeof DELIVERY_ACTIONS)[keyof typeof DELIVERY_ACTIONS];
	isDesktop: boolean;
}

const DeliveryCTA: React.FC<DeliveryCTAProps> = ({ deliveryAction, isDesktop }) => {
	const { id } = useVehicleData();

	const clickEventType = deliveryAction === DELIVERY_ACTIONS.BOOK ? 'book_delivery_button' : 'view_delivery_button';

	const label = `${LocalT.translate(`postSaleCTA.delivery.${isDesktop ? 'desktop' : 'mobile'}`, {
		action: LocalT.translate(`postSaleCTA.${deliveryAction}`),
	})}`;

	const handleDeliveryClick = () => {
		triggerPostSaleActionClickEvent({ enquiryId: id, eventType: clickEventType });
	};

	return (
		<Link legacyBehavior passHref as={ROUTES.DELIVERY.as(id)} href={ROUTES.DELIVERY.href}>
			<Button
				data-thc-button
				fullWidth
				as="a"
				label={label}
				onClick={handleDeliveryClick}
				{...applyCypressData(cypressIds.buttons.postSaleActions.deliveryAction)}
			/>
		</Link>
	);
};

export const PostSaleCTA: React.FC = () => {
	const { deliveryAction } = useDeliveryAction();

	const {
		minWidth: { breakpointTablet: isDesktop },
	} = useBreakpoint();

	return (
		<div className={styles.buttonContainer}>
			{deliveryAction ? <DeliveryCTA deliveryAction={deliveryAction} isDesktop={Boolean(isDesktop)} /> : null}
			<SellerDetails isDesktop={Boolean(isDesktop)} />
		</div>
	);
};
